import Vue from "vue";
import Vuex from "vuex";
import dayjs from "dayjs";
import moment from "moment";
const fb = require("./firebaseConfig.js");

Vue.use(Vuex);

export const store = new Vuex.Store({
  state: {
    currentUser: null,
    userRole: null,
    adminUser: null,
    weekStartDay: null,
    studyNameFilter: null,
    currentWeekStart: null,
    pastWeekStart: null,
    initialized: false,

    currentStudy: null,
    currentGraphData: null,
    currentParamsArray: [],
    currentAdminNotes: [],
    currentParticipantDetails: null,
    currentParamsApproved: null,

    sbVisible: false,
    sbMessage: null,

    dbAdminUsers: null,
    dbAdminParamsToReview: null,
    dbDevices: null,
    dbStudies: [],
    dbParameters: null,
    dbBolusLogs: null,
    dbBasalLogs: null,
    dbBgData: null,
    dbFeedback: null,
    dbLatestEntriesLogs: null,
    dbGroups: null,
    dbSurveys: null,
    dbMoodFeedback: null,
    dbStats: null
  },
  actions: {
    async fetchUserRole({ commit }) {
      fb.auth.currentUser
        .getIdTokenResult()
        .then(idTokenResult => {
          commit("setUserRole", idTokenResult.claims.role);
        })
        .catch(err => {
          console.log(err);
        });
    },
    async fetchInitialData({ commit, state, dispatch }) {
      const snapAdminUsers = await fb.AdminUsersRef.once("value");
      const adminUsers = snapAdminUsers.val();
      commit("setDbData", { key: "AdminUsers", val: adminUsers });

      const snapDevices = await fb.DevicesRef.once("value");
      commit("setDbData", { key: "Devices", val: snapDevices.val() });

      const snapSettingsWeekStart = await fb.AdminSettingsRef.child(
        "weekStartDay"
      ).once("value");
      commit("setWeekStartDay", snapSettingsWeekStart.val());

      const snapSettingsStudyName = await fb.AdminSettingsRef.child(
        "studyNameFilter"
      ).once("value");
      commit("setStudyNameFilter", snapSettingsStudyName.val());

      let adminUser;
      for (let id in adminUsers) {
        if (adminUsers[id].email === state.currentUser.email) {
          adminUser = adminUsers[id];
          adminUser["id"] = id;
        }
      }

      if (adminUser) {
        commit("setAdminUser", adminUser);
        fb.AdminUsersRef.child(adminUser.id)
          .child("lastSeen")
          .set(dayjs().toISOString());

        const snapAdminParamsToReview = await fb.AdminParamsToReviewRef.once(
          "value"
        );
        commit("setDbData", {
          key: "AdminParamsToReview",
          val: snapAdminParamsToReview.val()
        });

        const snapGroups = await fb.GroupsRef.once("value");
        commit("setDbData", { key: "Groups", val: snapGroups.val() });

        dispatch("fetchStudies", state.studyNameFilter);

        // commit("setInitialized");
      } else {
        dispatch("logout");
      }
    },

    fetchStudies({ commit }, studyName) {
      fb.StudiesRef.off();

      commit("clearStudies");

      if (!studyName) {
        fb.StudiesRef.on("child_added", snap =>
          commit("studiesChildAdded", snap)
        );
        fb.StudiesRef.on("child_changed", snap =>
          commit("studiesChildChanged", snap)
        );
        fb.StudiesRef.on("child_removed", snap =>
          commit("studiesChildRemoved", snap)
        );
      } else {
        fb.StudiesRef.orderByChild("name")
          .equalTo(studyName)
          .on("child_added", snap => commit("studiesChildAdded", snap));
        fb.StudiesRef.orderByChild("name")
          .equalTo(studyName)
          .on("child_changed", snap => commit("studiesChildChanged", snap));
        fb.StudiesRef.orderByChild("name")
          .equalTo(studyName)
          .on("child_removed", snap => commit("studiesChildRemoved", snap));
      }

      commit("setInitialized");
    },
    async fetchMoodFeedback({ state, commit }) {
      fb.MoodFeedbackRef.orderByChild("archived")
        .equalTo(null)
        .limitToLast(100)
        .on("value", moodSnap => {
          let data = moodSnap.val();
          for (let [key, study] of Object.entries(data)) {
            if ("study" in study && state.studyNameFilter) {
              if (study.study.split("_")[0] !== state.studyNameFilter) {
                delete data[key];
              }
            }
          }

          commit("setDbData", { key: "MoodFeedback", val: data });
        });

      const statsSnap = await fb.StatsRef.once("value");
      commit("setDbData", { key: "Stats", val: statsSnap.val() });
    },
    fetchFeedback({ state, commit }) {
      fb.FeedbackRef.orderByChild("archived")
        .equalTo(null)
        .limitToLast(10)
        .on("value", feedbackSnap => {
          let data = feedbackSnap.val();
          for (let [key, study] of Object.entries(data)) {
            if ("study" in study && state.studyNameFilter) {
              if (study.study.split("_")[0] !== state.studyNameFilter) {
                delete data[key];
              }
            }
          }

          commit("setDbData", { key: "Feedback", val: data });
        });
    },
    fetchGroups({ commit }) {
      fb.GroupsRef.once("value", snap => {
        commit("setDbData", { key: "Groups", val: snap.val() });
      });
    },
    async fetchUser({ state, commit }, studyID) {
      fb.AdminNotesRef.off();
      commit("clearCurrentStudy");

      let currentStudy = state.dbStudies.find(study => study.id === studyID);
      if (currentStudy) {
        commit("setCurrentStudy", currentStudy);

        fb.ParametersRef.child(studyID)
          .orderByChild("timestamp")
          .on("child_added", snap => commit("addedCurrentParamsArray", snap));
        fb.ParametersRef.child(studyID).on("child_changed", snap =>
          commit("changedCurrentParamsArray", snap)
        );

        fb.AdminNotesRef.child(studyID).on("child_added", snap =>
          commit("setCurrentAdminNotes", snap)
        );

        fb.AdminParticipantDetailsRef.child(studyID).once("value", snap =>
          commit("setCurrentParticipantDetails", snap.val())
        );

        fb.AdminParamsApprovedRef.child(studyID).once("value", snap =>
          commit("setCurrentParamsApproved", snap.val())
        );

        const graphDataSnap = await fb.GraphDataWeeklyRef.child(studyID)
          .orderByKey()
          .limitToLast(1)
          .once("value");
        commit("setCurrentGraphData", graphDataSnap.val());
      } else {
        alert("Error");
      }
    },

    pushAdminUser({ dispatch }, obj) {
      obj.timestamp = dayjs().toISOString();

      fb.AdminUsersRef.push(obj, error =>
        dispatch("handleFbResponse", { error })
      );
    },
    pushNote({ state, dispatch }, payload) {
      let obj = {
        note: payload.note,
        adminUserID: state.adminUser.id,
        flagged: payload.flagged || null,
        params: payload.params || null,
        timestamp: dayjs().toISOString()
      };

      fb.AdminNotesRef.child(payload.study).push(obj, error =>
        dispatch("handleFbResponse", { error })
      );
    },
    pushParams({ state, dispatch }, obj) {
      obj.timestamp = dayjs().toISOString();
      obj.updatedBy = "admin";
      obj.adminUserId = state.adminUser.id;
      obj.acknowledged = false;

      const studyId = state.currentStudy.id;
      const newParamsSnap = fb.ParametersRef.child(studyId).push(obj);
      fb.StudiesRef.child(studyId)
        .child("reviewParamsID")
        .set(newParamsSnap.key, error =>
          dispatch("handleFbResponse", { error })
        );

      if (obj.privateNotes) {
        let note = {
          note: obj.privateNotes,
          study: studyId,
          params: newParamsSnap.key
        };
        dispatch("pushNote", note);
      }
    },
    approveAlgoParams({ state, dispatch }, obj) {
      const studyId = state.currentStudy.id;
      const newParamsSnap = fb.ParametersRef.child(studyId).push(obj);
      fb.StudiesRef.child(studyId).update(
        {
          reviewParamsID: newParamsSnap.key,
          lastApprovedParamsID: newParamsSnap.key
        },
        error => dispatch("handleFbResponse", { error })
      );

      if (obj.privateNotes) {
        let note = {
          note: obj.privateNotes,
          study: studyId,
          params: newParamsSnap.key
        };
        dispatch("pushNote", note);
      }

      fb.AdminParamsToReviewRef.child(studyId)
        .child("approvedParamsID")
        .set(newParamsSnap.key);
    },
    saveParticipantDetails({ commit, state, dispatch }, obj) {
      const studyId = state.currentStudy.id;
      fb.AdminParticipantDetailsRef.child(studyId).update(obj, error =>
        dispatch("handleFbResponse", { error })
      );
      commit("setCurrentParticipantDetails", obj);
    },

    handleFbResponse({ commit }, payload) {
      if (payload.error) {
        commit("showSnackbar", { message: "Error" });
        console.log(payload.error);
      } else {
        commit("showSnackbar", { message: "Success" });
      }
    },

    logout({ commit }) {
      fb.auth.signOut().then(() => {
        commit("clear");
        this.$router.replace("login");
      });
    },
    askToSendMoodFeedback({ commit }, studyId) {
      fb.StudiesRef.child(studyId)
        .child("requiredToSendMoodFeedback")
        .set(true);
      commit("showSnackbar", { message: "Sent" });
    },
    askToOpenSurvey({ commit }, studyId) {
      fb.StudiesRef.child(studyId)
        .child("requiredToOpenSurvey")
        .set(true);
      commit("showSnackbar", { message: "Sent" });
    },
    addResetStudyRequest({ state, commit }, studyObj) {
      let obj = {
        study: studyObj.id,
        device: studyObj.device,
        admin: state.adminUser.id,
        timestamp: dayjs().toISOString()
      };
      fb.ResetRequestsRef.push(obj, error => {
        if (error) {
          commit("showSnackbar", {
            message:
              "There was an error. Make sure you have the necessary permissions to reset study."
          });
          console.error(error);
        } else {
          commit("showSnackbar", { message: "Reset request sent!" });
        }
      });
    },
    updateGroup({ commit }, { studyId, groupId, surveyId }) {
      fb.StudiesRef.child(studyId)
        .update({
          group: groupId,
          surveyId
        }, error => {
          if (error) {
            commit("showSnackbar", { message: "Error" });
            console.log(error);
          } else {
            commit("showSnackbar", { message: "Success" });
          }
        });
    },
    saveSettings({ state, commit, dispatch }, form) {
      const prevStudyNameFilter = state.studyNameFilter;

      fb.AdminSettingsRef.update(
        {
          studyNameFilter: form.studyNameFilter,
          weekStartDay: form.weekStartDay
        },
        error => {
          if (error) {
            commit("showSnackbar", { message: "Error" });
            console.log(error);
          } else {
            commit("showSnackbar", { message: "Success" });
            commit("setWeekStartDay", form.weekStartDay);
            commit("setStudyNameFilter", form.studyNameFilter);

            if (prevStudyNameFilter != form.studyNameFilter) {
              dispatch("fetchStudies", form.studyNameFilter);
            }
          }
        }
      );
    },
    saveNotificationsSettings({ dispatch }, notifs) {
      fb.AdminSettingsRef.child("notifications").update(notifs, error =>
        dispatch("handleFbResponse", { error })
      );
    },
    saveSurveys({ dispatch }, obj) {
      fb.SurveysRef.set(obj, error => dispatch("handleFbResponse", { error }));
    },
    sendResetPasswordEmail({ commit }, email) {
      fb.auth
        .sendPasswordResetEmail(email)
        .then(() => {
          commit("showSnackbar", { message: "Email sent" });
        })
        .catch(() => {
          commit("showSnackbar", {
            message: "There was an error sending the email"
          });
        });
    },
    deleteAdminUser({ commit }, obj) {
      fb.AdminUsersRef.child(obj.uid)
        .child("deleted")
        .set(true, error => {
          if (error) {
            commit("showSnackbar", { message: "Error deleting user" });
          } else {
            commit("showSnackbar", { message: "Successfully deleted user" });
          }
        });
    },
    updateAdminUser({ commit }, obj) {
      fb.AdminUsersRef.child(obj.id)
        .child("name")
        .set(obj.name, error => {
          if (error) {
            commit("showSnackbar", { message: "Error updating user" });
          } else {
            commit("showSnackbar", { message: "Successfully updated" });
            fb.AdminUsersRef.once("value", snap => {
              commit("setDbData", { key: "AdminUsers", val: snap.val() });
            });
          }
        });
    },

    archiveFeedback({ dispatch }, itemId) {
      fb.FeedbackRef.child(itemId).update(
        {
          archived: true
        },
        error => dispatch("handleFbResponse", { error })
      );
    },
    archiveMoodFeedback({ dispatch }, itemId) {
      fb.MoodFeedbackRef.child(itemId).update(
        {
          archived: true
        },
        error => dispatch("handleFbResponse", { error })
      );
    },

    listenersOffUser({ state, commit }) {
      const studyId = state.currentStudy.id;

      fb.ParametersRef.child(studyId).off();
      fb.AdminNotesRef.child(studyId).off();

      commit("clearCurrentStudy");
    },

    showSnackbar({ commit }, message) {
      commit("showSnackbar", { message });
    }
  },
  mutations: {
    clear(state) {
      state.currentUser = null;
      state.userRole = null;
      state.adminUser = null;
      state.weekStartDay = null;
      state.studyNameFilter = null;
      state.currentWeekStart = null;
      state.pastWeekStart = null;
      state.initialized = false;

      state.currentStudy = null;
      state.currentGraphData = null;
      state.currentParamsArray = [];
      state.currentAdminNotes = [];
      state.currentParticipantDetails = null;
      state.currentParamsApproved = null;

      state.dbAdminUsers = null;
      state.dbAdminParamsToReview = null;
      state.dbDevices = null;
      state.dbStudies = [];
      state.dbParameters = null;
      state.dbBolusLogs = null;
      state.dbBasalLogs = null;
      state.dbBgData = null;
      state.dbFeedback = null;
      state.dbLatestEntriesLogs = null;
      state.dbGroups = null;
      state.dbSurveys = null;
      state.dbMoodFeedback = null;
      state.dbStats = null;
    },

    showSnackbar(state, { message }) {
      state.sbMessage = message;
      state.sbVisible = true;
      setTimeout(() => (state.sbVisible = false), 4000);
    },

    setCurrentUser(state, val) {
      state.currentUser = val;
    },
    setUserRole(state, val) {
      state.userRole = val;
    },
    setAdminUser(state, val) {
      state.adminUser = val;
    },
    setWeekStartDay(state, val) {
      state.weekStartDay = val;

      if (moment().isoWeekday() >= val) {
        state.currentWeekStart = moment()
          .isoWeekday(val)
          .format("YYYY-MM-DD");
      } else {
        state.currentWeekStart = moment()
          .subtract(1, "weeks")
          .isoWeekday(val)
          .format("YYYY-MM-DD");
      }

      if (moment().isoWeekday() >= val) {
        state.pastWeekStart = moment()
          .subtract(1, "weeks")
          .isoWeekday(val)
          .format("YYYY-MM-DD");
      } else {
        state.pastWeekStart = moment()
          .subtract(2, "weeks")
          .isoWeekday(val)
          .format("YYYY-MM-DD");
      }
    },
    setStudyNameFilter(state, val) {
      state.studyNameFilter = val;
    },
    setInitialized(state) {
      state.initialized = true;
    },

    setDbData(state, payload) {
      state[`db${payload.key}`] = payload.val;
    },
    clearStudies(state) {
      state.dbStudies = [];
    },
    updateAdminUser(state, form) {
      state.adminUser = Object.assign(state.adminUser, form);
    },

    clearCurrentStudy(state) {
      state.currentStudy = null;
      state.currentGraphData = null;
      state.currentParamsArray = [];
      state.currentAdminNotes = [];
      state.currentParticipantDetails = null;
      state.currentParamsApproved = null;
    },
    setCurrentStudy(state, payload) {
      state.currentStudy = payload;
    },
    setCurrentAdminNotes(state, snap) {
      let obj = snap.val();
      obj.id = snap.key;
      state.currentAdminNotes.push(obj);
    },
    setCurrentGraphData(state, data) {
      if (data) {
        state.currentGraphData = data;
      } else {
        state.currentGraphData = "none";
      }
    },
    setCurrentParamsArray(state, snap) {
      snap.forEach(curSnap => {
        let params = curSnap.val();
        params.id = curSnap.key;
        state.currentParamsArray.unshift(params);
      });
    },
    setCurrentParticipantDetails(state, obj) {
      state.currentParticipantDetails = obj;
    },
    setCurrentParamsApproved(state, obj) {
      state.currentParamsApproved = obj;
    },

    addedCurrentParamsArray(state, snap) {
      let paramsIndex = state.currentParamsArray.findIndex(
        paramsObj => paramsObj.id === snap.key
      );
      let deleteNb = 1;
      if (paramsIndex < 0) {
        paramsIndex = 0;
        deleteNb = 0;
      }

      let params = snap.val();
      params.id = snap.key;
      state.currentParamsArray.splice(paramsIndex, deleteNb, params);
    },
    changedCurrentParamsArray(state, snap) {
      let paramsIndex = state.currentParamsArray.findIndex(
        paramsObj => paramsObj.id === snap.key
      );
      if (paramsIndex >= 0) {
        state.currentParamsArray.splice(paramsIndex, 1);

        let params = snap.val();
        params.id = snap.key;
        state.currentParamsArray.splice(paramsIndex, 0, params);
      }
    },

    async studiesChildAdded(state, snap) {
      let study = snap.val();
      study.id = snap.key;

      if (study.currentParamsID) {
        const snapParams = await fb.ParametersRef.child(study.id)
          .orderByChild("timestamp")
          .limitToLast(1)
          .once("value");
        if (snapParams.numChildren() > 0) {
          study.params = Object.values(snapParams.val())[0];
        }
      }

      const snapLatestEntries = await fb.LatestEntriesLogsRef.child(study.id)
        .orderByKey()
        .limitToLast(1)
        .once("value");
      if (snapLatestEntries.numChildren() > 0) {
        study.latestLog = Object.keys(snapLatestEntries.val())[0] * 1000;
      }

      study["needsReview"] =
        state.dbAdminParamsToReview && study.id in state.dbAdminParamsToReview
          ? true
          : false;

      state.dbStudies.push(study);
    },
    studiesChildRemoved(state, snap) {
      let studyIndex = state.dbStudies.findIndex(
        study => study.id === snap.key
      );
      if (studyIndex >= 0) {
        state.dbStudies.splice(studyIndex, 1);
      }
    },
    async studiesChildChanged(state, snap) {
      let study = snap.val();
      study.id = snap.key;

      let studyIndex = state.dbStudies.findIndex(item => item.id === study.id);
      if (studyIndex >= 0) {
        let oldStudy = state.dbStudies[studyIndex];
        if (study.currentParamsID !== oldStudy.currentParamsID) {
          const snapParams = await fb.ParametersRef.child(study.id)
            .orderByChild("timestamp")
            .limitToLast(1)
            .once("value");
          if (snapParams.numChildren() > 0) {
            study.params = Object.values(snapParams.val())[0];
          }
        } else {
          study.params = oldStudy.params;
        }

        if (oldStudy.latestLog) {
          study.latestLog = oldStudy.latestLog;
        }

        study.rowShouldAnimateUpdate = true;

        state.dbStudies.splice(studyIndex, 1, study);

        setTimeout(() => {
          study.rowShouldAnimateUpdate = false;
        }, 2 * 1000);
      }
    }
  }
});
